import {
  CollapsibleNavSectionConfig,
  AppId,
  NavContext,
  CollapsibleNavConfig,
} from '../../interfaces';

// Path templates for each nav item
const NAV_PATHS: Record<string, string> = {
  // Core Settings
  'core-settings-relevance': '/sites/relevance',
  'core-settings-boost': '/sites/boost',
  'core-settings-typeahead': '/sites/typeahead',
  // Commerce Studio
  'commerce-studio-editor': '/applications/em/${emId}/ranking-rules/search-and-browse/visual-editor',
  'commerce-studio-rules': '/applications/em/${emId}/ranking-rules',
  'commerce-studio-rewrites': '/applications/em/${emId}/rewrite',
  'commerce-studio-pages': '/applications/em/${emId}/pages',
  // Agent Studio
  'agent-studio-agents': '/agent-studio/agents?workspaceId=${workspaceId}',
  'agent-studio-library': '/agent-studio/library?workspaceId=${workspaceId}',
  // Analytics Studio
  'analytics-studio-dashboard': '/signals/${signalStoreId}',
  'analytics-studio-integrations': '/signals/${signalStoreId}/integrations/?tab=Signals+Using+Beacon',
  'analytics-studio-agents': '/signals/${signalStoreId}/agents',
  // UI Studio
  'ui-studio-builder': '/builder',
};

const buildPath = (template: string, context: NavContext): string => {
  return template
    .replace(/\$\{workspaceId\}/g, context.workspaceId || '')
    .replace(/\$\{siteId\}/g, context.siteId || '')
    .replace(/\$\{siteEnvironmentId\}/g, context.siteEnvironmentId || '')
    .replace(/\$\{emId\}/g, context.experienceManagerInstanceId || '')
    .replace(/\$\{signalStoreId\}/g, context.signalStoreId || '')
    .replace(/\$\{appStudioId\}/g, context.appStudioId || '');
};

const buildCoreSettingsPath = (basePath: string, context: NavContext): string => {
  const params = new URLSearchParams();
  if (context.siteId) params.set('siteId', context.siteId);
  if (context.workspaceId) params.set('workspaceId', context.workspaceId);
  if (context.siteEnvironmentId) params.set('siteEnvironmentId', context.siteEnvironmentId);
  return `${basePath}?${params.toString()}`;
};

const createHandlePath = (
  cssClass: string,
  appId: AppId,
  hostAppId: AppId,
  context: NavContext,
  navigate: (path: string) => void
): (() => void) => {
  const pathTemplate = NAV_PATHS[cssClass];
  if (!pathTemplate) return () => {};

  const isInternal = hostAppId === appId;

  return () => {
    let path: string;

    if (appId === 'core-settings') {
      path = buildCoreSettingsPath(pathTemplate, context);
    } else {
      path = buildPath(pathTemplate, context);
    }

    if (isInternal) {
      navigate(path);
    } else {
      globalThis.location.href = globalThis.location.origin + path;
    }
  };
};

export const getLwCollapsibleNavItems = (config: CollapsibleNavConfig): CollapsibleNavSectionConfig[] => {
  const {
    hostAppId,
    capabilities,
    context,
    navigate,
    collapsedNavHeaders = [],
    selectedPath = '',
  } = config;

  const createHandler = (cssClass: string, appId: AppId) =>
    createHandlePath(cssClass, appId, hostAppId, context, navigate);

  return [
    capabilities.showCoreSettings && {
      topLevelLabel: 'Core Settings',
      cssClass: 'core-settings',
      collapsible: true,
      collapsed: collapsedNavHeaders.includes('Core Settings'),
      navItems: [
        {
          cssClass: 'core-settings-relevance',
          text: 'Relevance',
          selected: selectedPath.includes('relevance'),
          handlePath: createHandler('core-settings-relevance', 'core-settings'),
        },
        {
          cssClass: 'core-settings-boost',
          text: 'Boost',
          selected: selectedPath.includes('boost'),
          handlePath: createHandler('core-settings-boost', 'core-settings'),
        },
        ...(capabilities.showTypeahead
          ? [
              {
                cssClass: 'core-settings-typeahead',
                text: 'Typeahead',
                selected: selectedPath.includes('typeahead'),
                handlePath: createHandler('core-settings-typeahead', 'core-settings'),
              },
            ]
          : []),
      ],
    },
    capabilities.showCommerceStudio && {
      topLevelLabel: 'Commerce Studio',
      cssClass: 'commerce-studio',
      collapsible: true,
      collapsed: collapsedNavHeaders.includes('Commerce Studio'),
      navItems: [
        {
          cssClass: 'commerce-studio-editor',
          text: 'Editor',
          selected: selectedPath.includes('visual-editor'),
          handlePath: createHandler('commerce-studio-editor', 'commerce-studio'),
        },
        {
          cssClass: 'commerce-studio-rules',
          text: 'Rules',
          selected: selectedPath.includes('ranking-rules') && !selectedPath.includes('visual-editor'),
          handlePath: createHandler('commerce-studio-rules', 'commerce-studio'),
        },
        {
          cssClass: 'commerce-studio-rewrites',
          text: 'Rewrites',
          selected: selectedPath.includes('rewrite'),
          handlePath: createHandler('commerce-studio-rewrites', 'commerce-studio'),
        },
        {
          cssClass: 'commerce-studio-pages',
          text: 'Pages',
          selected: selectedPath.includes('pages'),
          handlePath: createHandler('commerce-studio-pages', 'commerce-studio'),
        },
      ],
    },
    capabilities.showAgentStudio && {
      topLevelLabel: 'Agent Studio',
      cssClass: 'agent-studio',
      collapsible: true,
      collapsed: collapsedNavHeaders.includes('Agent Studio'),
      navItems: [
        {
          cssClass: 'agent-studio-agents',
          text: 'Agents',
          selected: selectedPath.includes('agent-studio/agents'),
          handlePath: createHandler('agent-studio-agents', 'agent-studio'),
          displayCount: async (stores: any) => {
            if (stores?.appStudioStore) {
              return stores.appStudioStore.data?.length || '';
            }
            return '';
          },
        },
        {
          cssClass: 'agent-studio-library',
          text: 'Library',
          selected: selectedPath.includes('agent-studio/library'),
          handlePath: createHandler('agent-studio-library', 'agent-studio'),
          displayCount: async (stores: any) => {
            if (stores?.appStudioStore) {
              const components = await stores.appStudioStore.getComponents();
              return components?.length || '';
            }
            return '';
          },
        },
      ],
    },
    capabilities.showAnalyticsStudio && {
      topLevelLabel: 'Analytics Studio',
      cssClass: 'analytics-studio',
      collapsible: true,
      collapsed: collapsedNavHeaders.includes('Analytics Studio'),
      navItems: [
        {
          cssClass: 'analytics-studio-dashboard',
          text: 'Dashboard',
          selected: selectedPath.includes('signals') && !selectedPath.includes('integrations') && !selectedPath.includes('agents'),
          handlePath: createHandler('analytics-studio-dashboard', 'analytics-studio'),
        },
        {
          cssClass: 'analytics-studio-integrations',
          text: 'Integrations',
          selected: selectedPath.includes('integrations'),
          handlePath: createHandler('analytics-studio-integrations', 'analytics-studio'),
        },
        {
          cssClass: 'analytics-studio-agents',
          text: 'Agents',
          selected: selectedPath.includes('signals') && selectedPath.includes('agents'),
          handlePath: createHandler('analytics-studio-agents', 'analytics-studio'),
        },
      ],
    },
    capabilities.showUIStudio && {
      topLevelLabel: 'UI Studio',
      cssClass: 'ui-studio',
      collapsible: true,
      collapsed: collapsedNavHeaders.includes('UI Studio'),
      navItems: [
        {
          cssClass: 'ui-studio-builder',
          text: 'Builder',
          // document.baseURI.pathname is `/` locally and `/ui-studio/` on deployed.
          selected: selectedPath.startsWith(`${new URL(document.baseURI).pathname}builder`),
          // Re-click on /builder/<id> returns to the list at /builder.
          alwaysClickable: true,
          handlePath: createHandler('ui-studio-builder', 'ui-studio'),
        },
      ],
    },
  ].filter(Boolean) as CollapsibleNavSectionConfig[];
};
